// src/services/facilityService.js
import api from '../api';

/**
 * Service for handling facility operations within the group context
 */
export const facilityService = {
  /**
   * Get all facilities for a group
   * @param {string} groupId - The ID of the group
   * @returns {Promise} Promise with the facilities data
   */
  getFacilities: async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}/facilities`);
      // Handle different response structures
      // Some APIs return { data: [...] } while others return the array directly
      if (response && response.data) {
        return Array.isArray(response.data) ? response.data : response.data.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching facilities:', error);
      throw error;
    }
  },

  /**
   * Get listings for a facility
   * @param {string} facilityId - The ID of the facility
   * @returns {Promise} Promise with the listings data
   */
  getListingsForFacility: async (facilityId) => {
    try {
      const response = await api.get(`/facilities/${facilityId}/listings`);
      if (response && response.data) {
        return Array.isArray(response.data) ? response.data : (response.data.data || []);
      }
      return [];
    } catch (error) {
      console.error('Error fetching listings for facility:', error);
      throw error;
    }
  },

  /**
   * Add a new facility to a group
   * @param {string} groupId - The ID of the group
   * @param {Object} facilityData - The facility data to add
   * @returns {Promise} Promise with the created facility data
   */
  addFacility: async (groupId, facilityData) => {
    return api.post(`/groups/${groupId}/facilities`, facilityData);
  },

  /**
   * Update a facility within a group
   * @param {string} groupId - The ID of the group
   * @param {string} facilityId - The ID of the facility to update
   * @param {Object} updateData - The facility data to update
   * @returns {Promise} Promise with the updated facility data
   */
  updateFacility: async (groupId, facilityId, updateData) => {
    return api.put(`/groups/${groupId}/facilities/${facilityId}`, updateData);
  },

  /**
   * Remove a facility from a group
   * @param {string} groupId - The ID of the group
   * @param {string} facilityId - The ID of the facility to remove
   * @returns {Promise} Promise with the result of the deletion
   */
  removeFacility: async (groupId, facilityId) => {
    return api.delete(`/groups/${groupId}/facilities/${facilityId}`);
  },

  /**
   * Update a specific field on a facility
   * @param {string} groupId - The ID of the group
   * @param {string} facilityId - The ID of the facility to update
   * @param {string} fieldPath - The field name (can be nested using dot notation e.g., 'contact.email')
   * @param {*} value - The new value for the field
   * @returns {Promise} Promise with the updated facility data
   */
  updateFacilityField: async (groupId, facilityId, fieldPath, value) => {
    // Create an object with the nested field structure
    const updateData = {};
    
    // Special cases for facilityDetails fields - backend expects them at root level
    if (fieldPath === 'facilityDetails.facilityType') {
      // Set facilityType at root level as required by backend
      updateData.facilityType = value;
    } else if (fieldPath === 'facilityDetails.traumaLevel') {
      // Set traumaLevel at root level as required by backend
      updateData.traumaLevel = value;
    } else if (fieldPath === 'facilityDetails.teamModel') {
      // Set teamModel at root level as required by backend
      updateData.teamModel = value;
    } else if (fieldPath.includes('.')) {
      const parts = fieldPath.split('.');
      let current = updateData;
      
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = {};
        current = current[parts[i]];
      }
      
      current[parts[parts.length - 1]] = value;
    } else {
      updateData[fieldPath] = value;
    }
    
    console.log(`Updating facility ${facilityId} with data:`, updateData);
    
    // Use the correct endpoint for updating facility details
    return api.put(`/facilities/${facilityId}`, updateData);
  }
};

export default facilityService;
